import React, { useEffect, useMemo, useState, useRef, Fragment } from 'react';
import { Shield, Search, AlertTriangle, CheckCircle2, Clock, Filter, Download, Plus, Edit2, Play, FileText, X, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useScreenInit } from '../useScreenInit.js';
// --- MOCK DATA ---
const generateTrendData = () => {
  const data = [];
  const today = new Date();
  for (let i = 30; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    data.push({
      date: date.toLocaleDateString('it-IT', {
        day: '2-digit',
        month: 'short'
      }),
      Bloccate: Math.floor(Math.random() * 15) + 5,
      Mascherate: Math.floor(Math.random() * 25) + 10,
      Loggate: Math.floor(Math.random() * 40) + 20
    });
  }
  return data;
};
const TREND_DATA = generateTrendData();
const LOGS_DATA = [{
  id: 'l1',
  timestamp: '27 Mar 2026, 14:32',
  service: 'Customer Assistant',
  dataType: 'Codice Fiscale',
  action: 'Bloccata',
  severity: 'Critico'
}, {
  id: 'l2',
  timestamp: '27 Mar 2026, 14:15',
  service: 'Smart Matcher',
  dataType: 'Email',
  action: 'Mascherata',
  severity: 'Warning'
}, {
  id: 'l3',
  timestamp: '27 Mar 2026, 13:45',
  service: 'Resume Screener',
  dataType: 'Dati Sanitari',
  action: 'Bloccata',
  severity: 'Critico'
}, {
  id: 'l4',
  timestamp: '27 Mar 2026, 12:30',
  service: 'Content Moderator',
  dataType: 'Nome/Cognome',
  action: 'Log Only',
  severity: 'Info'
}, {
  id: 'l5',
  timestamp: '27 Mar 2026, 11:20',
  service: 'Customer Assistant',
  dataType: 'IBAN',
  action: 'Mascherata',
  severity: 'Warning'
}, {
  id: 'l6',
  timestamp: '27 Mar 2026, 10:05',
  service: 'Document Analyzer',
  dataType: 'Numero Telefono',
  action: 'Log Only',
  severity: 'Info'
}, {
  id: 'l7',
  timestamp: '26 Mar 2026, 16:40',
  service: 'AI Tutor',
  dataType: 'Email',
  action: 'Mascherata',
  severity: 'Warning'
}, {
  id: 'l8',
  timestamp: '26 Mar 2026, 15:10',
  service: 'Resume Screener',
  dataType: 'Dati Biometrici',
  action: 'Bloccata',
  severity: 'Critico'
}, {
  id: 'l9',
  timestamp: '26 Mar 2026, 14:00',
  service: 'Smart Matcher',
  dataType: 'Indirizzo',
  action: 'Log Only',
  severity: 'Info'
}, {
  id: 'l10',
  timestamp: '26 Mar 2026, 11:45',
  service: 'Customer Assistant',
  dataType: 'Codice Fiscale',
  action: 'Bloccata',
  severity: 'Critico'
}];
const SCANS_DATA = [{
  id: 'sc1',
  service: 'Customer Assistant',
  date: '26 Mar 2026',
  high: 0,
  medium: 2,
  low: 5,
  status: 'Completata'
}, {
  id: 'sc2',
  service: 'Resume Screener',
  date: '25 Mar 2026',
  high: 1,
  medium: 4,
  low: 12,
  status: 'Da risolvere'
}, {
  id: 'sc3',
  service: 'Smart Matcher',
  date: '20 Mar 2026',
  high: 0,
  medium: 0,
  low: 3,
  status: 'Completata'
}, {
  id: 'sc4',
  service: 'Document Analyzer',
  date: '18 Mar 2026',
  high: 2,
  medium: 5,
  low: 8,
  status: 'Da risolvere'
}, {
  id: 'sc5',
  service: 'Content Moderator',
  date: '15 Mar 2026',
  high: 0,
  medium: 1,
  low: 4,
  status: 'Completata'
}, {
  id: 'sc6',
  service: 'AI Tutor',
  date: '10 Mar 2026',
  high: 0,
  medium: 0,
  low: 1,
  status: 'Completata'
}];
const DPO_DATA = [{
  id: 'd1',
  service: 'Customer Assistant',
  dpo: 'Dr. Laura Bianchi',
  contact: 'Mario Rossi',
  controller: 'SIAE SpA',
  updated: '15 Gen 2026'
}, {
  id: 'd2',
  service: 'Smart Matcher',
  dpo: 'Avv. Marco Conti',
  contact: 'Giulia Verdi',
  controller: 'SIAE SpA',
  updated: '20 Feb 2026'
}, {
  id: 'd3',
  service: 'Content Moderator',
  dpo: 'Dr. Laura Bianchi',
  contact: 'Luca Neri',
  controller: 'SIAE SpA',
  updated: '10 Mar 2026'
}, {
  id: 'd4',
  service: 'AI Tutor',
  dpo: 'Avv. Elena Ferrari',
  contact: 'Sofia Romano',
  controller: 'SIAE SpA',
  updated: '05 Mar 2026'
}, {
  id: 'd5',
  service: 'Resume Screener',
  dpo: 'Avv. Marco Conti',
  contact: 'Andrea Costa',
  controller: 'HR Dept',
  updated: '28 Feb 2026'
}, {
  id: 'd6',
  service: 'Document Analyzer',
  dpo: 'Dr. Laura Bianchi',
  contact: 'Elena Gallo',
  controller: 'Legal Dept',
  updated: '12 Gen 2026'
}];
const DATA_TYPES_DATA = [{
  id: 'dt1',
  name: 'Codice Fiscale',
  pattern: '^[A-Z]{6}\\d{2}[A-Z]\\d{2}[A-Z]\\d{3}[A-Z]$',
  severity: 'Alta',
  action: 'Blocca',
  services: 8,
  active: true
}, {
  id: 'dt2',
  name: 'IBAN',
  pattern: '^IT\\d{2}[A-Z]\\d{22}$',
  severity: 'Alta',
  action: 'Blocca',
  services: 5,
  active: true
}, {
  id: 'dt3',
  name: 'Email',
  pattern: '^[\\w-\\.]+@([\\w-]+\\.)+[\\w-]{2,4}$',
  severity: 'Media',
  action: 'Maschera',
  services: 12,
  active: true
}, {
  id: 'dt4',
  name: 'Numero Telefono',
  pattern: '^\\+?\\d{10,13}$',
  severity: 'Bassa',
  action: 'Solo Log',
  services: 10,
  active: true
}, {
  id: 'dt5',
  name: 'Dati Sanitari',
  pattern: '(diagnosi|patologia|ricovero|terapia)',
  severity: 'Alta',
  action: 'Blocca',
  services: 2,
  active: true
}, {
  id: 'dt6',
  name: 'Dati Biometrici',
  pattern: '(impronta|volto|iride|voce)',
  severity: 'Alta',
  action: 'Blocca',
  services: 1,
  active: true
}, {
  id: 'dt7',
  name: 'Indirizzo',
  pattern: '(via|piazza|viale|corso)\\s+[a-zA-Z\\s]+,\\s*\\d+',
  severity: 'Media',
  action: 'Maschera',
  services: 6,
  active: false
}, {
  id: 'dt8',
  name: 'Nome/Cognome',
  pattern: '^[A-Z][a-z]+\\s[A-Z][a-z]+$',
  severity: 'Bassa',
  action: 'Solo Log',
  services: 12,
  active: true
}];
// --- COMPONENTS ---
export function GDPRPage() {
  const screenInit = useScreenInit();
  const [activeTab, setActiveTab] = useState(screenInit.activeTab ?? 'Monitoraggio');
  const [period, setPeriod] = useState('30G');
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Modal Form State
  const [newTypeName, setNewTypeName] = useState('');
  const [newTypePattern, setNewTypePattern] = useState('');
  const [newTypeSeverity, setNewTypeSeverity] = useState('Media');
  const [newTypeAction, setNewTypeAction] = useState('Maschera');
  const [newTypeDesc, setNewTypeDesc] = useState('');
  // Scan simulation state
  const [isScanning, setIsScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [scanComplete, setScanComplete] = useState(false);
  const [selectedRepo, setSelectedRepo] = useState('Customer Assistant (repo: cust-assist-ai)');
  const scanIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const SCAN_RESULTS = {
    service: 'Customer Assistant',
    date: new Date().toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }),
    duration: '2m 34s',
    filesScanned: 147,
    high: 1,
    medium: 3,
    low: 5,
    findings: [{
      severity: 'Alta',
      file: 'src/handlers/userQuery.ts',
      line: 87,
      desc: 'Dati utente (email, nome) inviati al provider LLM senza anonimizzazione preventiva.',
      rule: 'GDPR Art. 25 - Data Protection by Design'
    }, {
      severity: 'Media',
      file: 'src/services/contextBuilder.ts',
      line: 203,
      desc: 'Il contesto della conversazione include lo storico completo senza limite temporale di retention.',
      rule: 'GDPR Art. 5(1)(e) - Limitazione della conservazione'
    }, {
      severity: 'Media',
      file: 'src/middleware/logging.ts',
      line: 45,
      desc: 'I log delle richieste contengono payload completi con possibili dati personali in chiaro.',
      rule: 'GDPR Art. 32 - Sicurezza del trattamento'
    }, {
      severity: 'Media',
      file: 'src/utils/exportData.ts',
      line: 112,
      desc: 'Funzione di export CSV non applica filtri sui campi sensibili prima del download.',
      rule: 'GDPR Art. 5(1)(f) - Integrità e riservatezza'
    }, {
      severity: 'Bassa',
      file: 'src/config/providers.ts',
      line: 23,
      desc: 'Manca documentazione esplicita sulla base giuridica del trattamento per ciascun provider.',
      rule: 'GDPR Art. 6 - Liceità del trattamento'
    }, {
      severity: 'Bassa',
      file: 'src/components/ChatWidget.tsx',
      line: 156,
      desc: 'Il banner di consenso non specifica le finalità di trattamento AI in modo granulare.',
      rule: 'GDPR Art. 7 - Condizioni per il consenso'
    }, {
      severity: 'Bassa',
      file: 'src/services/analytics.ts',
      line: 78,
      desc: 'Metriche di utilizzo aggregate includono user-agent e IP senza pseudonimizzazione.',
      rule: 'GDPR Art. 4(5) - Pseudonimizzazione'
    }, {
      severity: 'Bassa',
      file: 'src/api/feedback.ts',
      line: 34,
      desc: 'Endpoint di feedback non implementa il diritto alla cancellazione dei commenti utente.',
      rule: 'GDPR Art. 17 - Diritto alla cancellazione'
    }, {
      severity: 'Bassa',
      file: 'src/hooks/useUserProfile.ts',
      line: 91,
      desc: 'Profilo utente caricato senza verifica del consenso aggiornato al trattamento.',
      rule: 'GDPR Art. 7(3) - Revoca del consenso'
    }]
  };
  const startScan = () => {
    setIsScanning(true);
    setScanProgress(0);
    setScanComplete(false);
    scanIntervalRef.current = setInterval(() => {
      setScanProgress(prev => {
        if (prev >= 100) {
          if (scanIntervalRef.current) clearInterval(scanIntervalRef.current);
          setIsScanning(false);
          setScanComplete(true);
          return 100;
        }
        // Variable speed: slower at start and end, faster in middle
        const increment = prev < 20 ? 2 : prev < 80 ? 4 : 2;
        return Math.min(prev + increment, 100);
      });
    }, 150);
  };
  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (scanIntervalRef.current) clearInterval(scanIntervalRef.current);
    };
  }, []);
  const filteredLogs = useMemo(() => {
    return LOGS_DATA.filter(l => l.service.toLowerCase().includes(searchQuery.toLowerCase()) || l.dataType.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [searchQuery]);
  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case 'Critico':
      case 'Alta':
        return <span className="bg-red-50 text-red-600 border border-red-100 rounded-full px-2.5 py-0.5 text-[11px] font-medium" data-id="element-2673" data-mp="3ov3qmmy0zh7t">
            Critico
          </span>;
      case 'Warning':
      case 'Media':
        return <span className="bg-amber-50 text-amber-600 border border-amber-100 rounded-full px-2.5 py-0.5 text-[11px] font-medium" data-id="element-2674" data-mp="3fybeq7362mf1">
            Warning
          </span>;
      case 'Info':
      case 'Bassa':
        return <span className="bg-blue-50 text-blue-600 border border-blue-100 rounded-full px-2.5 py-0.5 text-[11px] font-medium" data-id="element-2675" data-mp="3tsccoymao5ec">
            Info
          </span>;
      default:
        return null;
    }
  };
  const getActionBadge = (action: string) => {
    switch (action) {
      case 'Bloccata':
      case 'Blocca':
        return <span className="bg-red-50 text-red-600 border border-red-100 rounded-full px-2.5 py-0.5 text-[11px] font-medium flex items-center gap-1" data-id="element-2676" data-mp="2nd1n8vd0mxxb">
            <Shield className="w-3 h-3" data-id="element-2677" data-mp="2fubwym5wbtnv" /> Blocca
          </span>;
      case 'Mascherata':
      case 'Maschera':
        return <span className="bg-amber-50 text-amber-600 border border-amber-100 rounded-full px-2.5 py-0.5 text-[11px] font-medium flex items-center gap-1" data-id="element-2678" data-mp="2osxd6anwawlh">
            <EyeOff className="w-3 h-3" data-id="element-2679" data-mp="2juvtpomk4v9h" /> Maschera
          </span>;
      case 'Log Only':
      case 'Solo Log':
        return <span className="bg-blue-50 text-blue-600 border border-blue-100 rounded-full px-2.5 py-0.5 text-[11px] font-medium flex items-center gap-1" data-id="element-2680" data-mp="1w25jpky7sm1u">
            <FileText className="w-3 h-3" data-id="element-2681" data-mp="25vcwvrz756ch" /> Solo Log
          </span>;
      default:
        return null;
    }
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Completata':
        return <span className="bg-emerald-50 text-emerald-600 border border-emerald-100 rounded-full px-2.5 py-0.5 text-[11px] font-medium flex items-center gap-1" data-id="element-2682" data-mp="1dcfq3166u6bi">
            <CheckCircle2 className="w-3 h-3" data-id="element-2683" data-mp="38howckjqxi6i" /> Completata
          </span>;
      case 'In corso':
        return <span className="bg-blue-50 text-blue-600 border border-blue-100 rounded-full px-2.5 py-0.5 text-[11px] font-medium flex items-center gap-1" data-id="element-2684" data-mp="3dfqft6l33jii">
            <Clock className="w-3 h-3" data-id="element-2685" data-mp="3i930j5mn765z" /> In corso
          </span>;
      case 'Da risolvere':
        return <span className="bg-amber-50 text-amber-600 border border-amber-100 rounded-full px-2.5 py-0.5 text-[11px] font-medium flex items-center gap-1" data-id="element-2686" data-mp="3n85tvst9fny4">
            <AlertTriangle className="w-3 h-3" data-id="element-2687" data-mp="2uigshecn1lvz" /> Da risolvere
          </span>;
      default:
        return null;
    }
  };
  return <div className="min-h-screen bg-[#F4F6F8] p-4 md:p-8 relative" data-id="element-2688" data-mp="2zexi6czhk8b6">
      {/* 1. HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6" data-id="element-2689" data-mp="33jnchxehxetz">
        <div data-id="element-2690" data-mp="epq5fd84luyb">
          <h1 className="text-[28px] md:text-[32px] font-semibold text-[#1A202C] tracking-tight" data-id="element-2691" data-mp="escfbjihomjp">
            GDPR Compliance
          </h1>
        </div>
        <div className="flex items-center bg-white border border-gray-200 rounded-lg p-1 shadow-sm" data-id="element-2692" data-mp="23a5k6a3mqrpa">
          {['7G', '30G', '90G', '12M'].map(p => <button key={p} onClick={() => setPeriod(p)} className={`px-3 py-1.5 text-[12px] font-medium rounded-md transition-colors ${period === p ? 'bg-[#F4F6F8] text-[#2A3B5C] shadow-sm' : 'text-gray-500 hover:text-[#1A202C]'}`} data-id="element-2693" data-mp="1z93og38x8xs2">
              {p}
            </button>)}
        </div>
      </div>

      {/* 2. TABS NAVIGATION */}
      <div className="border-b-2 border-gray-300 mb-8" data-id="element-2694" data-mp="1fkkwjdwj769o">
        <div className="-mb-[2px] flex items-center gap-8 overflow-x-auto custom-scrollbar" data-id="element-2695" data-mp="1firru45haw4b">
          {['Monitoraggio', 'Scansione Repository', 'DPO & Responsabili', 'Dati Sensibili'].map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`pb-3 text-[15px] font-medium transition-colors whitespace-nowrap border-b-2 ${activeTab === tab ? 'border-[#00B4D8] text-[#2A3B5C]' : 'border-transparent text-gray-500 hover:text-gray-800'}`} data-id="element-2696" data-mp="16ky3pghvb8s8">
              {tab}
            </button>)}
        </div>
      </div>

      {/* 3. TAB CONTENT */}

      {/* TAB 1: MONITORAGGIO */}
      {activeTab === 'Monitoraggio' && <div className="space-y-6" data-id="element-2697" data-mp="22jh36om1xfqc">
          {/* Area Chart */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6" data-id="element-2698" data-mp="1olad3uncgk18">
            <div className="flex justify-between items-center mb-6" data-id="element-2699" data-mp="1tk3tbuu0mp53">
              <h2 className="text-[16px] font-semibold text-[#1A202C]" data-id="element-2700" data-mp="1ewinvu9o4du0">
                Trend Intercettazioni Dati Sensibili
              </h2>
              <div className="flex items-center gap-4 text-[11px] font-medium" data-id="element-2701" data-mp="1jojxt9jd5sgv">
                <div className="flex items-center gap-1.5" data-id="element-2702" data-mp="15wlw3jrzeyuq">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500" data-id="element-2703" data-mp="19x5t8nck22gv"></div>
                  Bloccate
                </div>
                <div className="flex items-center gap-1.5" data-id="element-2704" data-mp="vx4r74kk0ozt">
                  <div className="w-2.5 h-2.5 rounded-full bg-amber-500" data-id="element-2705" data-mp="vzqyr47g34i1"></div>
                  Mascherate
                </div>
                <div className="flex items-center gap-1.5" data-id="element-2706" data-mp="ha61be1ci2n7">
                  <div className="w-2.5 h-2.5 rounded-full bg-blue-500" data-id="element-2707" data-mp="hcsafi3vwq7j"></div>
                  Loggate
                </div>
              </div>
            </div>
            <div className="h-[280px] w-full" data-id="element-2708" data-mp="r9wvfelzufmr">
              <ResponsiveContainer width="100%" height="100%" data-id="element-2709" data-mp="r6iw6f8mmmjj">
                <AreaChart data={TREND_DATA} margin={{
              top: 10,
              right: 10,
              left: -20,
              bottom: 0
            }} data-id="element-2710" data-mp="m87kdyyru1yo">
                  <defs data-id="element-2711" data-mp="vb8qw0rjtv2v">
                    <linearGradient id="colorBloccate" x1="0" y1="0" x2="0" y2="1" data-id="element-2712" data-mp="v9pa891zdi0k">
                      <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} data-id="element-2713" data-mp="19ybmg1wb9tnk" />
                      <stop offset="95%" stopColor="#ef4444" stopOpacity={0} data-id="element-2714" data-mp="14zqns6wpji18" />
                    </linearGradient>
                    <linearGradient id="colorMascherate" x1="0" y1="0" x2="0" y2="1" data-id="element-2715" data-mp="1j132kf04tdlj">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} data-id="element-2716" data-mp="1eyzm1bylvite" />
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} data-id="element-2717" data-mp="1m1o00gsy8eir" />
                    </linearGradient>
                    <linearGradient id="colorLoggate" x1="0" y1="0" x2="0" y2="1" data-id="element-2718" data-mp="1r07rewqbaiii">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} data-id="element-2719" data-mp="1w03v0uv3k7xj" />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} data-id="element-2720" data-mp="1vxzglizknqj0" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-2721" data-mp="24sxcmyy0njek" />
                  <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#6B7280'
              }} dy={10} minTickGap={30} data-id="element-2722" data-mp="29qpf2mwaenx0" />
                  <YAxis axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#6B7280'
              }} data-id="element-2723" data-mp="2jp4wfy5iiitb" />
                  <Tooltip contentStyle={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                border: '1px solid #E5E7EB',
                fontSize: '12px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }} itemStyle={{
                color: '#1A202C',
                fontWeight: 500
              }} data-id="element-2724" data-mp="kdve99d2vlqe" />
                  <Area type="monotone" dataKey="Loggate" stackId="1" stroke="#3b82f6" fill="url(#colorLoggate)" strokeWidth={2} data-id="element-2725" data-mp="2tsjw68n4cn8j" />
                  <Area type="monotone" dataKey="Mascherate" stackId="1" stroke="#f59e0b" fill="url(#colorMascherate)" strokeWidth={2} data-id="element-2726" data-mp="2ow3b5nb7uozo" />
                  <Area type="monotone" dataKey="Bloccate" stackId="1" stroke="#ef4444" fill="url(#colorBloccate)" strokeWidth={2} data-id="element-2727" data-mp="3hixqy6x3906t" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Logs Table */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col" data-id="element-2728" data-mp="37k87watgh9oz">
            <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-4" data-id="element-2729" data-mp="212l4s6lgce0p">
              <h2 className="text-[16px] font-semibold text-[#1A202C]" data-id="element-2730" data-mp="2g7ag1hsuzk">
                Log Intercettazioni
              </h2>
              <div className="flex items-center gap-3 w-full sm:w-auto" data-id="element-2731" data-mp="51t67z5n1sii">
                <div className="relative w-full sm:w-64" data-id="element-2732" data-mp="3m9oabboxwpsf">
                  <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-2733" data-mp="3mb7rd4if74v9" />
                  <input type="text" placeholder="Cerca servizio o tipo dato..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-[13px] focus:outline-none focus:border-[#00B4D8] focus:ring-1 focus:ring-[#00B4D8] transition-all" data-id="element-2734" data-mp="3r99go76fw0f6" />
                </div>
                <button className="p-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-500 hover:text-[#1A202C] hover:bg-gray-100 transition-colors" data-id="element-2735" data-mp="e1pn3elsdn30">
                  <Filter className="w-4 h-4" data-id="element-2736" data-mp="11ppcob7d37g0" />
                </button>
                <button className="p-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-500 hover:text-[#1A202C] hover:bg-gray-100 transition-colors" data-id="element-2737" data-mp="16jadsc494hjf">
                  <Download className="w-4 h-4" data-id="element-2738" data-mp="2ma48ba4tn4tl" />
                </button>
              </div>
            </div>
            <div className="overflow-x-auto" data-id="element-2739" data-mp="30z8qwsfj3b28">
              <table className="w-full text-left border-collapse" data-id="element-2740" data-mp="30xxp2y5n8ddr">
                <thead data-id="element-2741" data-mp="2w0p4kqx0vx9c">
                  <tr className="bg-gray-50/50 border-b border-gray-100" data-id="element-2742" data-mp="2w05vlzpcoj7g">
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2743" data-mp="28b4jo2hb6pxf">
                      Timestamp
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2744" data-mp="2hahy79kai4p0">
                      Servizio
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2745" data-mp="2db93a1dik0rv">
                      Tipo Dato
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2746" data-mp="3onjmbg2ggij4">
                      Azione
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2747" data-mp="39gcnhvafqy9e">
                      Severità
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-2748" data-mp="3ig9gg93uyja9">
                  {filteredLogs.map(log => <tr key={log.id} className="hover:bg-gray-50/50 transition-colors" data-id="element-2749" data-mp="aebh1d9hndr">
                      <td className="px-6 py-4" data-id="element-2750" data-mp="3seyola5ycpdb">
                        <span className="text-[13px] text-gray-500" data-id="element-2751" data-mp="3se74owlpwg2v">
                          {log.timestamp}
                        </span>
                      </td>
                      <td className="px-6 py-4" data-id="element-2752" data-mp="51ctq6ht8vjn">
                        <span className="text-[13px] font-medium text-[#2a3a54]" data-id="element-2753" data-mp="53pltcha8maf">
                          {log.service}
                        </span>
                      </td>
                      <td className="px-6 py-4" data-id="element-2754" data-mp="f1dhc4mppxtn">
                        <span className="text-[13px] text-[#1A202C]" data-id="element-2755" data-mp="ylvlgv2mcwy7">
                          {log.dataType}
                        </span>
                      </td>
                      <td className="px-6 py-4" data-id="element-2756" data-mp="12l3aariw4zac">
                        {getActionBadge(log.action)}
                      </td>
                      <td className="px-6 py-4" data-id="element-2757" data-mp="tm96ag38d6mr">
                        {getSeverityBadge(log.severity)}
                      </td>
                    </tr>)}
                  {filteredLogs.length === 0 && <tr data-id="element-2758" data-mp="jobj7vx3xfvq">
                      <td colSpan={5} className="px-6 py-8 text-center text-[13px] text-gray-500" data-id="element-2759" data-mp="jouzl873zw7n">
                        Nessun log trovato per "{searchQuery}"
                      </td>
                    </tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>}

      {/* TAB 2: SCANSIONE REPOSITORY */}
      {activeTab === 'Scansione Repository' && <div className="space-y-6" data-id="element-2760" data-mp="xkfpazswjp9">
          {/* Top Actions */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-2761" data-mp="zy799v9vds2">
            <div className="flex-1 w-full" data-id="element-2762" data-mp="396v29db1hyry">
              <label className="block text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-2763" data-mp="3961t65olyx6n">
                Seleziona Repository / Servizio
              </label>
              <select value={selectedRepo} onChange={e => setSelectedRepo(e.target.value)} disabled={isScanning} className="w-full sm:w-96 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-[13px] text-[#1A202C] focus:outline-none focus:border-[#00B4D8] focus:ring-1 focus:ring-[#00B4D8] disabled:opacity-50" data-id="element-2764" data-mp="2zbjn4tvpypjo">
                <option data-id="element-2765" data-mp="3n146vezp5qu0">Customer Assistant (repo: cust-assist-ai)</option>
                <option data-id="element-2766" data-mp="3hzojvhdyla8z">Smart Matcher (repo: smart-matcher-core)</option>
                <option data-id="element-2767" data-mp="2bl768tihxigy">Content Moderator (repo: content-mod-v2)</option>
                <option data-id="element-2768" data-mp="26lazwnmbtey8">AI Tutor (repo: ai-tutor-platform)</option>
                <option data-id="element-2769" data-mp="26n5nyasw9n6j">Resume Screener (repo: resume-screen-v3)</option>
                <option data-id="element-2770" data-mp="m4bk4tqd6un4">Document Analyzer (repo: doc-analyzer)</option>
              </select>
            </div>
            <button onClick={startScan} disabled={isScanning} className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 bg-[#2A3B5C] text-white rounded-lg hover:bg-[#1E2A40] transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2771" data-mp="r2mztl3cbzd9">
              <Play className="w-4 h-4" data-id="element-2772" data-mp="15py89e487395" />
              <span className="text-[14px] font-medium" data-id="element-2773" data-mp="15t2ias4dgt7q">
                {isScanning ? 'Scansione in corso...' : 'Avvia Scansione'}
              </span>
            </button>
          </div>

          {/* Scan in Progress */}
          {isScanning && <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 shadow-sm" data-id="element-2774" data-mp="1jramnl3qmwsx">
              <div className="flex justify-between items-center mb-4" data-id="element-2775" data-mp="3gjat0s5h296k">
                <div className="flex items-center gap-3" data-id="element-2776" data-mp="3gldss3wffgoh">
                  <div className="p-2 bg-blue-100 rounded-lg" data-id="element-2777" data-mp="3lix5v0he1neu">
                    <Search className="w-5 h-5 text-blue-600 animate-pulse" data-id="element-2778" data-mp="1ftlfvu6dw6md" />
                  </div>
                  <div data-id="element-2779" data-mp="1av0madln0l6w">
                    <h3 className="text-[15px] font-semibold text-blue-900" data-id="element-2780" data-mp="1atpabqi1g74p">
                      Scansione in corso: {selectedRepo.split(' (')[0]}
                    </h3>
                    <p className="text-[12px] text-blue-700 mt-0.5" data-id="element-2781" data-mp="1kt7ua6xy7swr">
                      {scanProgress < 30 ? 'Analisi struttura del progetto e dipendenze...' : scanProgress < 60 ? 'Scansione codice sorgente per pattern di dati sensibili...' : scanProgress < 85 ? 'Verifica best practice GDPR e compliance...' : 'Generazione report e classificazione findings...'}
                    </p>
                  </div>
                </div>
                <span className="text-[14px] font-bold text-blue-700" data-id="element-2782" data-mp="1145pwgeog57q">
                  {scanProgress}%
                </span>
              </div>
              <div className="w-full bg-blue-200/50 rounded-full h-2" data-id="element-2783" data-mp="9drhv0tchn3z">
                <div className="bg-blue-600 h-2 rounded-full transition-all duration-200 ease-out" style={{
            width: `${scanProgress}%`
          }} data-id="element-2784" data-mp="9dj5da14wjtb"></div>
              </div>
              <div className="flex items-center gap-4 mt-3 text-[11px] text-blue-600" data-id="element-2785" data-mp="41ckib3mmsyn">
                <span data-id="element-2786" data-mp="3li414x59zrt0">
                  File analizzati: {Math.floor(scanProgress * 1.47)}/147
                </span>
                <span data-id="element-2787" data-mp="3qgnon1zipbnh">•</span>
                <span data-id="element-2788" data-mp="3qdt4lbkf1sul">Tempo trascorso: {Math.floor(scanProgress * 0.15)}s</span>
              </div>
            </div>}

          {/* Scan Complete - Results */}
          {scanComplete && <div className="space-y-4" data-id="element-2789" data-mp="mynkjxx31qf5">
              {/* Summary Card */}
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6" data-id="element-2790" data-mp="81czpxyjv86r">
                <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-6" data-id="element-2791" data-mp="d07ttw1ywj66">
                  <div className="flex items-center gap-3" data-id="element-2792" data-mp="2src45xgdyz4m">
                    <div className="p-2 bg-emerald-100 rounded-lg" data-id="element-2793" data-mp="2xrgoxzobug0n">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" data-id="element-2794" data-mp="2xsrsbydv1lr8" />
                    </div>
                    <div data-id="element-2795" data-mp="147y5xkko3h2r">
                      <h3 className="text-[16px] font-semibold text-[#1A202C]" data-id="element-2796" data-mp="z9oar9yy0gh5">
                        Scansione completata: {selectedRepo.split(' (')[0]}
                      </h3>
                      <p className="text-[12px] text-gray-500 mt-0.5" data-id="element-2797" data-mp="ugv9wx6lhsac">
                        {SCAN_RESULTS.date} • Durata: {SCAN_RESULTS.duration} •{' '}
                        {SCAN_RESULTS.filesScanned} file analizzati
                      </p>
                    </div>
                  </div>
                  <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-[13px] font-medium" data-id="element-2798" data-mp="gl9iykgswyfa">
                    <Download className="w-4 h-4" data-id="element-2799" data-mp="6mk28v0n5k0m" />
                    Esporta Report
                  </button>
                </div>

                {/* Severity Summary */}
                <div className="grid grid-cols-3 gap-4" data-id="element-2800" data-mp="20yqqauemvvac">
                  <div className="bg-red-50 border border-red-100 rounded-lg p-4 text-center" data-id="element-2801" data-mp="20x7b774prqa5">
                    <p className="text-[28px] font-bold text-red-600" data-id="element-2802" data-mp="28gov4llvei78">
                      {SCAN_RESULTS.high}
                    </p>
                    <p className="text-[12px] font-medium text-red-700 mt-1" data-id="element-2803" data-mp="2i82pkeyysrti">
                      Criticità Alta
                    </p>
                  </div>
                  <div className="bg-amber-50 border border-amber-100 rounded-lg p-4 text-center" data-id="element-2804" data-mp="2n99wkbta9mz4">
                    <p className="text-[28px] font-bold text-amber-600" data-id="element-2805" data-mp="1pm1jn57clm8q">
                      {SCAN_RESULTS.medium}
                    </p>
                    <p className="text-[12px] font-medium text-amber-700 mt-1" data-id="element-2806" data-mp="1zhl9paz8dqpk">
                      Criticità Media
                    </p>
                  </div>
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-center" data-id="element-2807" data-mp="3ax09emitqwod">
                    <p className="text-[28px] font-bold text-blue-600" data-id="element-2808" data-mp="3fw335atctgh1">
                      {SCAN_RESULTS.low}
                    </p>
                    <p className="text-[12px] font-medium text-blue-700 mt-1" data-id="element-2809" data-mp="4vsunqx06jwn">
                      Criticità Bassa
                    </p>
                  </div>
                </div>
              </div>

              {/* Findings Detail */}
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-id="element-2810" data-mp="emnj91xq1vl9">
                <div className="px-6 py-5 border-b border-gray-100" data-id="element-2811" data-mp="9rrdz03h5bq7">
                  <h2 className="text-[16px] font-semibold text-[#1A202C]" data-id="element-2812" data-mp="njxw2rq6tssa">
                    Dettaglio Findings ({SCAN_RESULTS.findings.length})
                  </h2>
                </div>
                <div className="divide-y divide-gray-100" data-id="element-2813" data-mp="xi3u2y8vierv">
                  {SCAN_RESULTS.findings.map((finding, idx) => {
              const iconBg = finding.severity === 'Alta' ? 'bg-red-50 border-red-100' : finding.severity === 'Media' ? 'bg-amber-50 border-amber-100' : 'bg-blue-50 border-blue-100';
              const badgeClass = finding.severity === 'Alta' ? 'bg-red-50 text-red-600 border-red-100' : finding.severity === 'Media' ? 'bg-amber-50 text-amber-600 border-amber-100' : 'bg-blue-50 text-blue-600 border-blue-100';
              const iconColor = finding.severity === 'Alta' ? 'text-red-500' : finding.severity === 'Media' ? 'text-amber-500' : 'text-blue-500';
              return <div key={idx} className="px-6 py-4 hover:bg-gray-50/50 transition-colors" data-id="element-2814" data-mp="15260v1ug7qme" data-mpi={idx}>
                        <div className="flex items-start gap-4" data-id="element-2815" data-mp="r2wvkx7s6gx7">
                          <div className={`mt-0.5 p-1.5 rounded-md border shrink-0 ${iconBg}`} data-id="element-2816" data-mp="w29ifhynjnzs">
                            {finding.severity === 'Alta' ? <AlertTriangle className={`w-4 h-4 ${iconColor}`} data-id="element-2817" data-mp="h5s9syhukngx" /> : finding.severity === 'Media' ? <AlertCircle className={`w-4 h-4 ${iconColor}`} data-id="element-2818" data-mp="m3tufnux8uui" /> : <FileText className={`w-4 h-4 ${iconColor}`} data-id="element-2819" data-mp="m4bwx286g5av" />}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-2820" data-mp="2bjt9fll81ei2">
                            <div className="flex items-center gap-3 mb-1 flex-wrap" data-id="element-2821" data-mp="2gh1qtjyjpei9">
                              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide border ${badgeClass}`} data-id="element-2822" data-mp="3hr9zp3touo4b">
                                {finding.severity}
                              </span>
                              <code className="text-[12px] bg-gray-100 text-gray-700 px-2 py-0.5 rounded font-mono border border-gray-200" data-id="element-2823" data-mp="3qqn3bfv4sih4">
                                {finding.file}{' '}
                                <span className="text-gray-400" data-id="element-2824" data-mp="4b29l2i3xun4">
                                  :{finding.line}
                                </span>
                              </code>
                            </div>
                            <p className="text-[13px] text-[#1A202C] mt-1.5" data-id="element-2825" data-mp="ddbhrlpwlbxc">
                              {finding.desc}
                            </p>
                            <p className="text-[11px] text-gray-500 mt-1 flex items-center gap-1" data-id="element-2826" data-mp="2f4oo8f7irrvi">
                              <Shield className="w-3 h-3" data-id="element-2827" data-mp="2k26olh7h0yrw" /> {finding.rule}
                            </p>
                          </div>
                        </div>
                      </div>;
            })}
                </div>
              </div>
            </div>}

          {/* Scans Table - always visible */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-id="element-2828" data-mp="22up7j3711g94">
            <div className="px-6 py-5 border-b border-gray-100" data-id="element-2829" data-mp="1xvm1bffb93zk">
              <h2 className="text-[16px] font-semibold text-[#1A202C]" data-id="element-2830" data-mp="2vi2ymko1nhlb">
                Storico Scansioni
              </h2>
            </div>
            <div className="overflow-x-auto" data-id="element-2831" data-mp="2qkjjlijiv0sb">
              <table className="w-full text-left border-collapse" data-id="element-2832" data-mp="2lk5by1lu1sut">
                <thead data-id="element-2833" data-mp="2lic2yfnqywjn">
                  <tr className="bg-gray-50/50 border-b border-gray-100" data-id="element-2834" data-mp="2llzytlgnzv1j">
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2835" data-mp="2hiabt0fn1lim">
                      Servizio
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2836" data-mp="162bvqcm1lz7w">
                      Data Scansione
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-center" data-id="element-2837" data-mp="1cjdedpql1l5l">
                      Criticità Alta
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-center" data-id="element-2838" data-mp="12lh4105gyu8j">
                      Criticità Media
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-center" data-id="element-2839" data-mp="os80hg47d2xx">
                      Criticità Bassa
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2840" data-mp="ets7f7lm7jls">
                      Stato
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-2841" data-mp="5w9j9ucocp5o">
                      Azioni
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-2842" data-mp="3nefbu3gvvepu">
                  {SCANS_DATA.map(scan => <Fragment key={scan.id} data-id="element-2843" data-mp="3ig435w2bra97">
                      <tr className="hover:bg-gray-50/50 transition-colors group" data-id="element-2844" data-mp="968uku20jyw">
                        <td className="px-6 py-4" data-id="element-2845" data-mp="73czk67zwm9">
                          <span className="text-[13px] font-medium text-[#2a3a54]" data-id="element-2846" data-mp="3refszvvda40l">
                            {scan.service}
                          </span>
                        </td>
                        <td className="px-6 py-4" data-id="element-2847" data-mp="592espmbalh3">
                          <span className="text-[13px] text-gray-500" data-id="element-2848" data-mp="566hn3iag0tp">
                            {scan.date}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center" data-id="element-2849" data-mp="ezdhy9y68qmx">
                          <span className={`text-[13px] font-bold ${scan.high > 0 ? 'text-red-600' : 'text-gray-400'}`} data-id="element-2850" data-mp="sxbv5c0rah0u">
                            {scan.high}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center" data-id="element-2851" data-mp="17ztu2qbnvxg4">
                          <span className={`text-[13px] font-bold ${scan.medium > 0 ? 'text-amber-600' : 'text-gray-400'}`} data-id="element-2852" data-mp="1c2gliq4f7gcs">
                            {scan.medium}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center" data-id="element-2853" data-mp="1lsjif1enxege">
                          <span className={`text-[13px] font-bold ${scan.low > 0 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-2854" data-mp="p20pzkpaxhh5">
                            {scan.low}
                          </span>
                        </td>
                        <td className="px-6 py-4" data-id="element-2855" data-mp="y25vtqeuk7t7">
                          {getStatusBadge(scan.status)}
                        </td>
                        <td className="px-6 py-4 text-right" data-id="element-2856" data-mp="133d5xw4gpj31">
                          <button className="text-[12px] font-medium text-[#00B4D8] hover:text-[#0096b4] transition-colors" data-id="element-2857" data-mp="29idrr8zrwkvw">
                            {scan.high > 0 || scan.medium > 0 ? 'Vedi Dettagli' : 'Report'}
                          </button>
                        </td>
                      </tr>
                      {/* Expanded row mock for one item to show findings pattern */}
                      {scan.id === 'sc2' && <tr className="bg-gray-50/30 border-b border-gray-100" data-id="element-2858" data-mp="3p84vs06xn7qo">
                          <td colSpan={7} className="px-6 py-4" data-id="element-2859" data-mp="3kamwl1ipw6vv">
                            <div className="bg-white border border-red-100 rounded-lg p-4 shadow-sm" data-id="element-2860" data-mp="3k893uzbif6rh">
                              <h4 className="text-[12px] font-bold text-red-800 uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-2861" data-mp="3g65fja3qs7os">
                                <AlertCircle className="w-4 h-4" data-id="element-2862" data-mp="369iv2mfbvina" /> Findings
                                Critici (1)
                              </h4>
                              <div className="flex flex-col gap-2" data-id="element-2863" data-mp="31dc17s07hxlp">
                                <div className="flex items-start gap-3 p-3 bg-red-50/50 rounded-md border border-red-100/50" data-id="element-2864" data-mp="14ho0xkp47wst">
                                  <div className="mt-0.5" data-id="element-2865" data-mp="zhrs985h3h6x">
                                    <FileText className="w-4 h-4 text-red-500" data-id="element-2866" data-mp="2tz1kuljk56wo" />
                                  </div>
                                  <div data-id="element-2867" data-mp="2ysdxdxaypn92">
                                    <p className="text-[13px] font-medium text-[#1A202C] font-mono" data-id="element-2868" data-mp="2k1wvkcuf0vbn">
                                      src/services/resumeParser.ts{' '}
                                      <span className="text-gray-400 font-normal" data-id="element-2869" data-mp="2oyx6b127mm93">
                                        line 142
                                      </span>
                                    </p>
                                    <p className="text-[12px] text-gray-600 mt-1" data-id="element-2870" data-mp="33pwalzw0iomv">
                                      Dati biometrici (foto profilo) estratti e
                                      salvati in chiaro nel database senza
                                      cifratura a riposo.
                                    </p>
                                    <p className="text-[11px] text-red-600 font-medium mt-1" data-id="element-2871" data-mp="1wckopjmk8zci">
                                      Violazione GDPR Art. 32 - Sicurezza del
                                      trattamento
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>}
                    </Fragment>)}
                </tbody>
              </table>
            </div>
          </div>
        </div>}

      {/* TAB 3: DPO & RESPONSABILI */}
      {activeTab === 'DPO & Responsabili' && <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-id="element-2872" data-mp="1fs4y1f3kzlt5">
          <div className="px-6 py-5 border-b border-gray-100 flex justify-between items-center" data-id="element-2873" data-mp="1fty9d7p52u7h">
            <h2 className="text-[16px] font-semibold text-[#1A202C]" data-id="element-2874" data-mp="1azk66j48jis9">
              Governance per Servizio
            </h2>
            <div className="relative w-64" data-id="element-2875" data-mp="id975cwa1pxk">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-2876" data-mp="ic7syzjysnbm" />
              <input type="text" placeholder="Cerca servizio o persona..." className="w-full pl-9 pr-4 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-[13px] focus:outline-none focus:border-[#00B4D8]" data-id="element-2877" data-mp="3t0derk24pzxq" />
            </div>
          </div>
          <div className="overflow-x-auto" data-id="element-2878" data-mp="asxoldb3yxg1">
            <table className="w-full text-left border-collapse" data-id="element-2879" data-mp="atz2rqnf801z">
              <thead data-id="element-2880" data-mp="fssf3fqz1l0k">
                <tr className="bg-gray-50/50 border-b border-gray-100" data-id="element-2881" data-mp="frhaxetm6b8x">
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2882" data-mp="kqtv1rt3p47t">
                    Servizio
                  </th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2883" data-mp="pk6ihym1n4yz">
                    DPO Assegnato
                  </th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2884" data-mp="yk30jhdxh5l2">
                    Data Protection Contact
                  </th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2885" data-mp="2o05np2dce87b">
                    Titolare Trattamento
                  </th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2886" data-mp="2ez7em5o1rahv">
                    Ultimo Aggiornamento
                  </th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-2887" data-mp="2a27cg5ubirw1">
                    Azioni
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-2888" data-mp="32r5xbapts851">
                {DPO_DATA.map(item => <tr key={item.id} className="hover:bg-gray-50/50 transition-colors group" data-id="element-2889" data-mp="2xw8lxx52pmoz">
                    <td className="px-6 py-4" data-id="element-2890" data-mp="2syqekb6pffj3">
                      <span className="text-[13px] font-medium text-[#2a3a54]" data-id="element-2891" data-mp="3qew5h84f8tny">
                        {item.service}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-2892" data-mp="3llt80owix02h">
                      <div className="flex items-center gap-2" data-id="element-2893" data-mp="1le8vignucwdu">
                        <div className="w-6 h-6 rounded-full bg-[#2A3B5C]/10 flex items-center justify-center text-[10px] font-bold text-[#2A3B5C]" data-id="element-2894" data-mp="1hdyp52w503yg">
                          {item.dpo.split(' ').map(n => n[0]).join('').replace('.', '')}
                        </div>
                        <span className="text-[13px] text-[#1A202C]" data-id="element-2895" data-mp="17lsz3ypg3wlw">
                          {item.dpo}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-2896" data-mp="xoeinz3iv7tl">
                      <span className="text-[13px] text-gray-600" data-id="element-2897" data-mp="tnupt7pzeqd9">
                        {item.contact}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-2898" data-mp="oqvwgre56ng4">
                      <span className="text-[13px] text-gray-600" data-id="element-2899" data-mp="2o2oriihddiq7">
                        {item.controller}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-2900" data-mp="2j17uiy20agew">
                      <span className="text-[13px] text-gray-400" data-id="element-2901" data-mp="2qljuey3xafjc">
                        {item.updated}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-2902" data-mp="30cehg4oztj5u">
                      <button className="p-1.5 text-gray-400 hover:text-[#00B4D8] hover:bg-blue-50 rounded-md transition-colors opacity-0 group-hover:opacity-100" data-id="element-2903" data-mp="30cwmnqtmv7pw">
                        <Edit2 className="w-4 h-4" data-id="element-2904" data-mp="27o8tl92n7e1s" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>}

      {/* TAB 4: DATI SENSIBILI */}
      {activeTab === 'Dati Sensibili' && <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-id="element-2905" data-mp="2ho9h777jogbs">
          <div className="px-6 py-5 border-b border-gray-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-2906" data-mp="2ll5l7cql4zjq">
            <div data-id="element-2907" data-mp="whw8abk44st">
              <h2 className="text-[16px] font-semibold text-[#1A202C]" data-id="element-2908" data-mp="vybkluiv2b3">
                Configurazione Dati Sensibili
              </h2>
              <p className="text-[12px] text-gray-500 mt-1" data-id="element-2909" data-mp="1xzqeez70zi6p">
                Definisci i pattern che il sistema deve intercettare prima di
                inviare dati ai provider AI.
              </p>
            </div>
            <button onClick={() => setIsModalOpen(true)} className="flex items-center gap-2 px-4 py-2 bg-[#2A3B5C] text-white rounded-lg hover:bg-[#1E2A40] transition-colors shadow-sm text-[13px] font-medium whitespace-nowrap" data-id="element-2910" data-mp="21zqqweakdjb4">
              <Plus className="w-4 h-4" data-id="element-2911" data-mp="1a9cjn0x2358f" />
              Aggiungi Tipo Dato
            </button>
          </div>
          <div className="overflow-x-auto" data-id="element-2912" data-mp="1o0hochfa6npm">
            <table className="w-full text-left border-collapse" data-id="element-2913" data-mp="1o1ka6axqxzyo">
              <thead data-id="element-2914" data-mp="1j7g742rlqc2v">
                <tr className="bg-gray-50/50 border-b border-gray-100" data-id="element-2915" data-mp="1j6wpkn5v5ppd">
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2916" data-mp="vhu5z9ro5ms8">
                    Nome
                  </th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2917" data-mp="3275nd0xljj36">
                    Pattern (Regex)
                  </th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2918" data-mp="3708jngtrd6n2">
                    Severità
                  </th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2919" data-mp="2y6vjpuxa14rh">
                    Azione Default
                  </th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-center" data-id="element-2920" data-mp="3pxrtgmo1itaj">
                    Servizi Attivi
                  </th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-2921" data-mp="3gwl922i9syc0">
                    Stato
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-2922" data-mp="25h541hfzxut9">
                {DATA_TYPES_DATA.map(type => <tr key={type.id} className={`hover:bg-gray-50/50 transition-colors ${!type.active ? 'opacity-60' : ''}`} data-id="element-2923" data-mp="2kr1pipj8wgjw">
                    <td className="px-6 py-4" data-id="element-2924" data-mp="2g035bev4yybd">
                      <span className="text-[13px] font-medium text-[#1A202C]" data-id="element-2925" data-mp="2fy8jzzfyd46r">
                        {type.name}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-2926" data-mp="270y7ybvb9b0s">
                      <code className="text-[11px] bg-gray-100 text-gray-600 px-2 py-1 rounded font-mono border border-gray-200" data-id="element-2927" data-mp="33pxhrvbjmkv3">
                        {type.pattern}
                      </code>
                    </td>
                    <td className="px-6 py-4" data-id="element-2928" data-mp="2uopv6aie38gu">
                      {getSeverityBadge(type.severity)}
                    </td>
                    <td className="px-6 py-4" data-id="element-2929" data-mp="2ppd0j3la0x3p">{getActionBadge(type.action)}</td>
                    <td className="px-6 py-4 text-center" data-id="element-2930" data-mp="1jacbziykzh75">
                      <span className="text-[13px] font-medium text-gray-600" data-id="element-2931" data-mp="1j7pw8w1jdvdu">
                        {type.services}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-2932" data-mp="148lxqkfqie3d">
                      {/* Simple Toggle Switch */}
                      <button className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${type.active ? 'bg-[#00B4D8]' : 'bg-gray-300'}`} data-id="element-2933" data-mp="1941bzdhnmc0b">
                        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${type.active ? 'translate-x-4' : 'translate-x-1'}`} data-id="element-2934" data-mp="1ixi4me621l2e" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>}

      {/* INLINE MODAL: Aggiungi Tipo Dato */}
      {isModalOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-2935" data-mp="21ti0w162fars">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity" onClick={() => setIsModalOpen(false)} data-id="element-2936" data-mp="2j9vblaap88p"></div>

          {/* Modal Content */}
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200" data-id="element-2937" data-mp="3vkosn2bttur7">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100" data-id="element-2938" data-mp="9e0ds54mhkkt">
              <h2 className="text-[18px] font-semibold text-[#1A202C]" data-id="element-2939" data-mp="4fgqo1eam2qv">
                Aggiungi Tipo Dato Sensibile
              </h2>
              <button onClick={() => setIsModalOpen(false)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-2940" data-mp="idncuk9vqeap">
                <X className="w-5 h-5" data-id="element-2941" data-mp="s4i0nt2rxly9" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-6 space-y-5" data-id="element-2942" data-mp="n554ghq0j2j0">
              <div data-id="element-2943" data-mp="n6ggf4tm3gl7">
                <label className="block text-[13px] font-medium text-gray-700 mb-1.5" data-id="element-2944" data-mp="n814cblf81a3">
                  Nome tipo dato *
                </label>
                <input type="text" placeholder="es. Numero Carta di Credito" value={newTypeName} onChange={e => setNewTypeName(e.target.value)} className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-[13px] focus:outline-none focus:border-[#00B4D8] focus:ring-1 focus:ring-[#00B4D8]" data-id="element-2945" data-mp="x4vxeeyxd9gz" />
              </div>

              <div data-id="element-2946" data-mp="16r6zdsp7bv3l">
                <label className="block text-[13px] font-medium text-gray-700 mb-1.5" data-id="element-2947" data-mp="16tt3tjgspuhl">
                  Pattern (Regex) *
                </label>
                <input type="text" placeholder="es. ^(?:4[0-9]{12}(?:[0-9]{3})?)$" value={newTypePattern} onChange={e => setNewTypePattern(e.target.value)} className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-[13px] font-mono focus:outline-none focus:border-[#00B4D8] focus:ring-1 focus:ring-[#00B4D8]" data-id="element-2948" data-mp="x0m2c9p6eyms" />
                <p className="text-[11px] text-gray-500 mt-1.5" data-id="element-2949" data-mp="s2u0amuthw4v">
                  Il pattern regex utilizzato per intercettare il dato nel
                  payload.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4" data-id="element-2950" data-mp="j14crs423hcd">
                <div data-id="element-2951" data-mp="yk5k5rmkgrtw">
                  <label className="block text-[13px] font-medium text-gray-700 mb-1.5" data-id="element-2952" data-mp="yjdmmb9esk0z">
                    Severità
                  </label>
                  <select value={newTypeSeverity} onChange={e => setNewTypeSeverity(e.target.value)} className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-[13px] focus:outline-none focus:border-[#00B4D8] focus:ring-1 focus:ring-[#00B4D8]" data-id="element-2953" data-mp="pj7b2ux9tu4g">
                    <option value="Alta" data-id="element-2954" data-mp="fmm9ykodh35w">Alta</option>
                    <option value="Media" data-id="element-2955" data-mp="att0xklvf8o0">Media</option>
                    <option value="Bassa" data-id="element-2956" data-mp="as87xzf7fxs9">Bassa</option>
                  </select>
                </div>
                <div data-id="element-2957" data-mp="5t5elc8l7g04">
                  <label className="block text-[13px] font-medium text-gray-700 mb-1.5" data-id="element-2958" data-mp="1tmq66hk4msj">
                    Azione Default
                  </label>
                  <select value={newTypeAction} onChange={e => setNewTypeAction(e.target.value)} className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-[13px] focus:outline-none focus:border-[#00B4D8] focus:ring-1 focus:ring-[#00B4D8]" data-id="element-2959" data-mp="3szegp6z8y1fv">
                    <option value="Blocca" data-id="element-2960" data-mp="28hdpv8m9qun1">Blocca chiamata</option>
                    <option value="Maschera" data-id="element-2961" data-mp="28jztix61gpzz">Maschera dato (***)</option>
                    <option value="Solo Log" data-id="element-2962" data-mp="2dhhwm6xdkb02">Solo Log</option>
                  </select>
                </div>
              </div>

              <div data-id="element-2963" data-mp="2hg8wumx508re">
                <label className="block text-[13px] font-medium text-gray-700 mb-1.5" data-id="element-2964" data-mp="1kskrl5i6cap9">
                  Descrizione
                </label>
                <textarea rows={3} placeholder="Descrizione opzionale dell'utilizzo..." value={newTypeDesc} onChange={e => setNewTypeDesc(e.target.value)} className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-[13px] focus:outline-none focus:border-[#00B4D8] focus:ring-1 focus:ring-[#00B4D8] resize-none" data-id="element-2965" data-mp="1ps741cq6hmx0" />
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3" data-id="element-2966" data-mp="1623prl1mniru">
              <button onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-[13px] font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-2967" data-mp="gsboi6ie4qd0">
                Annulla
              </button>
              <button onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-[13px] font-medium text-white bg-[#2A3B5C] rounded-lg hover:bg-[#1E2A40] transition-colors shadow-sm" data-id="element-2968" data-mp="brxi0swftoh3">
                Salva Tipo Dato
              </button>
            </div>
          </div>
        </div>}
    </div>;
}